import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { House, ListNumbers, GearSix, ShieldCheck, CalendarBlank, AddressBook } from '@phosphor-icons/react';

export default function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Bottom nav: Home | Events | Ladder | Directory | (Settings for members,
  // Admin for admins). Admins reach Settings via the Admin panel or /settings;
  // this keeps the nav readable at 5 items across roles.
  const navItems = [
    { path: '/', icon: House, label: 'Home' },
    { path: '/events', icon: CalendarBlank, label: 'Events' },
    { path: '/ladder', icon: ListNumbers, label: 'Ladder' },
    { path: '/directory', icon: AddressBook, label: 'Directory' },
    user?.role === 'admin'
      ? { path: '/admin', icon: ShieldCheck, label: 'Admin' }
      : { path: '/settings', icon: GearSix, label: 'Settings' },
  ];

  // Forest-green brand bar, anchored to the visible viewport bottom. The
  // outer <nav> is `fixed` (never detaches into mid-screen) and carries the
  // iPhone safe-area inset as its own bottom padding so the home indicator
  // never covers the icons. The inner row is a fixed 64px tall (matching
  // --rc-bottom-nav-h) so sticky action bars and page padding line up.
  return (
    <nav
      data-testid="bottom-nav"
      aria-label="Primary navigation"
      className="fixed bottom-0 left-0 right-0 z-50 max-w-md mx-auto sm:max-w-xl md:max-w-2xl lg:max-w-4xl bg-[#015e3f] border-t border-[#013523] shadow-[0_-6px_24px_rgba(1,53,35,0.28)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="flex justify-around items-stretch h-16 px-2">
        {navItems.map((item) => {
          // Home: exact match only. Events: also match /events/:slug. Others: exact.
          const isActive =
            location.pathname === item.path ||
            (item.path === '/events' && location.pathname.startsWith('/events'));
          return (
            <button
              key={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              aria-current={isActive ? 'page' : undefined}
              onClick={() => navigate(item.path)}
              className={`relative flex flex-1 flex-col items-center justify-center gap-0.5 transition-colors ${
                isActive ? 'text-white' : 'text-white/60 hover:text-white/90'
              }`}
            >
              {/* Active indicator — champagne-gold top bar */}
              {isActive && (
                <span className="absolute top-0 h-[3px] w-9 rounded-full bg-[#c2a251]" />
              )}
              <item.icon size={23} weight={isActive ? 'fill' : 'regular'} />
              <span className={`text-[10px] ${isActive ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
